import {
  Box,
  Stack,
  Table,
  Typography,
  CircularProgress,
  Link,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { GetEducationalVideo } from "@veris-health/virtual-doc-ms/lib/v1";
import {
  VrsConfirmationModals,
  VrsIconButton,
  IconName,
  VrsErrorMsg,
  dateFormats,
} from "@veris-health/web-core";
import dayjs from "dayjs";
import React, { useRef, useState } from "react";
import { useAppSelector } from "../../../hooks/useAppSelector";
import {
  StyledTableCell,
  StyledTableBody,
  StyledTableRow,
} from "../../../ui/components/Tables/shared/StyledTableElements";
import {
  Column,
  TableColumnNames,
  VrsSortedTableHeader,
} from "../../../ui/components/VrsSortedTableHeader";
import { selectEducationalVideos, selectEducationalVideosStatus } from "../educationalVideosSlice";

const columns: Column[] = [
  {
    id: TableColumnNames.Title,
    label: TableColumnNames.Title,
  },
  {
    id: TableColumnNames.Description,
    label: TableColumnNames.Description,
  },
  {
    id: TableColumnNames.Link,
    label: TableColumnNames.Link,
  },
  {
    id: TableColumnNames.Symptom,
    label: TableColumnNames.Symptom,
  },
  {
    id: TableColumnNames.DateUploaded,
    label: TableColumnNames.DateUploaded,
  },
  {
    id: TableColumnNames.Actions,
    label: TableColumnNames.Actions,
  },
];

const ActionsMenu = ({
  title,
  onEdit,
  onDelete,
}: {
  title: string;
  onDelete: () => void;
  onEdit: () => void;
}) => {
  const theme = useTheme();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState<boolean>(false);
  const [confimationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);

  return (
    <Stack direction="row" ref={anchorRef}>
      {confimationModalOpen && (
        <VrsConfirmationModals
          isOpen={confimationModalOpen}
          handleClose={() => setConfirmationModalOpen(false)}
          dialogHeader="Warning!"
          dialogContent={`You will delete "${title}" video.`}
          dialogSecondContentLine="Please confirm."
          onCancel={() => setConfirmationModalOpen(false)}
          onConfirm={() => {
            setConfirmationModalOpen(false);
            onDelete();
          }}
          cancelButtonText="Cancel"
          confirmButtonText="Continue"
        />
      )}

      <VrsIconButton
        iconProps={{ name: open ? IconName.ActiveDots : IconName.Dots }}
        onClick={() => setOpen(true)}
      />
      <Menu anchorEl={anchorRef.current} open={open} onClose={() => setOpen(false)}>
        <MenuItem
          onClick={() => {
            setConfirmationModalOpen(true);
            setOpen(false);
          }}
        >
          <Typography variant="body" color={theme.veris.colors.neutrals["grey-4"]}>
            Delete
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpen(false);
            onEdit();
          }}
        >
          <Typography variant="body" color={theme.veris.colors.neutrals["grey-4"]}>
            Edit
          </Typography>
        </MenuItem>
      </Menu>
    </Stack>
  );
};

const StyledTableColumn = ({
  column,
  video,
  onEdit,
  onDelete,
}: {
  column: Column;
  video: GetEducationalVideo;
  onDelete: () => void;
  onEdit: () => void;
}) => {
  const theme = useTheme();
  return (
    <StyledTableCell key={column.id}>
      {column.id === TableColumnNames.Title && (
        <Typography variant="subtitle1" color={theme.veris.colors.neutrals["grey-4"]}>
          {video.title || "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.Description && (
        <Typography
          variant="subtitle2"
          color={theme.veris.colors.neutrals["grey-4"]}
          sx={{ wordBreak: "break-word", maxWidth: "350px" }}
        >
          {video.description || "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.Link && (
        <Link href={video.link} underline="none" target="_blank">
          <Typography variant="subtitle1" color={theme.veris.colors.amethyst.normal}>
            {video.link}
          </Typography>
        </Link>
      )}
      {column.id === TableColumnNames.Symptom && (
        <Typography variant="subtitle1" color={theme.veris.colors.neutrals["grey-4"]}>
          {video.symptom || "N/A"}
        </Typography>
      )}
      {column.id === TableColumnNames.DateUploaded && (
        <Typography variant="subtitle2" color={theme.veris.colors.neutrals["grey-4"]}>
          {video.dateUploaded
            ? dayjs(video.dateUploaded).format(dateFormats["MMM DD, YYYY"])
            : "N/A"}
        </Typography>
      )}

      {column.id === TableColumnNames.Actions && (
        <ActionsMenu title={video.title} onEdit={onEdit} onDelete={onDelete} />
      )}
    </StyledTableCell>
  );
};

export const EducationalVideosTable = ({
  fetchEducationalVideos,
  onEdit,
  onDelete,
}: {
  fetchEducationalVideos: () => void;
  onEdit: (video: GetEducationalVideo) => void;
  onDelete: (video: GetEducationalVideo) => void;
}): JSX.Element => {
  const educationalVideos = useAppSelector(selectEducationalVideos);
  const educationalVideosStatus = useAppSelector(selectEducationalVideosStatus);

  return (
    <>
      {educationalVideosStatus === "loading" && (
        <Box height="50%" display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      )}
      {educationalVideosStatus === "failed" && (
        <VrsErrorMsg size="small" onClick={fetchEducationalVideos} />
      )}
      {educationalVideosStatus === "idle" && (
        <Box
          sx={{
            backgroundColor: (theme) => theme.veris.colors.neutrals.white,
            border: (theme) => `1px solid ${theme.veris.colors.neutrals["grey-light"]}`,
            borderRadius: (theme) => theme.shape.borderRadius,
            height: "73vh",
            overflow: "auto",
          }}
          px={2}
          pb={1}
        >
          {educationalVideos?.items?.length > 0 ? (
            <Table>
              <VrsSortedTableHeader columns={columns} />
              <StyledTableBody>
                {educationalVideos.items.map((video) => (
                  <StyledTableRow key={video.id}>
                    {columns.map((column) => (
                      <StyledTableColumn
                        column={column}
                        video={video}
                        key={column.id}
                        onEdit={() => onEdit(video)}
                        onDelete={() => onDelete(video)}
                      />
                    ))}
                  </StyledTableRow>
                ))}
              </StyledTableBody>
            </Table>
          ) : (
            <Typography
              color={(theme) => theme.veris.colors.neutrals["grey-3"]}
              textAlign="center"
              variant="body"
            >
              There are no educational videos at this moment.
            </Typography>
          )}
        </Box>
      )}
    </>
  );
};
